import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, IMG } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BedDouble, Bath, Ruler, MapPin, Home, CalendarDays, Phone, Mail, ShieldCheck, Heart, Sparkles, BadgeCheck, Rocket, Star, MessageCircle, Clock, XCircle, PlayCircle, Eye, MessageSquare, Flame, Images } from "lucide-react";
import BoostDialog from "@/components/BoostDialog";
import PropertyGalleryLightbox from "@/components/PropertyGalleryLightbox";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import ShareBar from "@/components/ShareBar";
import { fireLeadConversion } from "@/lib/ads";
import RelatedProperties from "@/components/RelatedProperties";
import ListingConcierge from "@/components/ListingConcierge";

const HERO_FALLBACK = "https://images.pexels.com/photos/13416122/pexels-photo-13416122.jpeg?auto=compress&cs=tinysrgb&h=650&w=940";

/** Long-form "posted N days/weeks/months ago" used on the detail page. */
function _timeAgoLong(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} week${w > 1 ? "s" : ""} ago`;
  }
  const m = Math.floor(days / 30);
  return `${m} month${m > 1 ? "s" : ""} ago`;
}

// Turn a raw video URL into an embed-friendly URL (YouTube, Instagram, direct MP4)
function embedUrlFor(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const host = u.hostname.replace("www.", "");
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = u.searchParams.get("v");
      if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return { kind: "iframe", src: `https://www.youtube.com/embed/${id}` };
    }
    if (host === "instagram.com" && u.pathname.match(/\/(reel|p)\//)) {
      return { kind: "iframe", src: `https://www.instagram.com/${u.pathname.replace(/^\//, "")}embed` };
    }
    if (/\.(mp4|webm|ogg|mov|m4v|3gp)$/i.test(u.pathname)) {
      return { kind: "video", src: raw };
    }
    return { kind: "iframe", src: raw };
  } catch {
    return null;
  }
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh, config } = useAuth();
  const boostOn = !!config?.boost_enabled;
  const [property, setProperty] = useState(null);
  const [contact, setContact] = useState(null); // {phone, email}
  const [revealing, setRevealing] = useState(false);
  // Lightbox: null = closed, otherwise the index of the photo to open at.
  const [lightboxAt, setLightboxAt] = useState(null);
  const [boostOpen, setBoostOpen] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProperty(data)).catch(() => nav("/properties"));
  }, [id, nav]);

  const reveal = async () => {
    if (!user) { nav("/login"); return; }
    setRevealing(true);
    try {
      const { data } = await api.post(`/properties/${id}/reveal`);
      setContact(data);
      fireLeadConversion();
      await refresh();
      toast.success(data.source === "free"
        ? "Contact revealed (free)"
        : data.source === "paid"
        ? "Contact revealed — 1 credit used"
        : "Contact revealed");
    } catch (e) {
      const msg = formatApiError(e);
      if (e?.response?.status === 402) {
        toast.error("Free reveals exhausted — buy a pack to continue");
        nav("/pricing");
      } else {
        toast.error(msg);
      }
    } finally { setRevealing(false); }
  };

  const toggleFav = async () => {
    if (!user) { nav("/login"); return; }
    try { await api.post(`/favorites/${id}`); await refresh(); }
    catch { toast.error("Could not update favorite"); }
  };

  if (!property) return <div className="p-20 text-center text-gray-500">Loading…</div>;
  const isRent = property.listing_type === "rent";
  const isOwner = user && user.id === property.owner_id;
  const images = property.image_paths?.length ? property.image_paths.map(IMG) : [HERO_FALLBACK];
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const shareImage = `${backend}/api/og/property/${property.id}.png`;
  const remaining = (user && !user.is_unlimited)
    ? Math.max(0, user.free_reveals_limit - user.free_reveals_used) + user.contacts_remaining
    : null;
  const isFav = user?.favorites?.includes(id);

  const priceLabel = property.price >= 10000000
    ? `₹${(property.price / 10000000).toFixed(2)} Cr`
    : property.price >= 100000
      ? `₹${(property.price / 100000).toFixed(2)} L`
      : `₹${property.price.toLocaleString("en-IN")}`;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-4 md:py-10">
      <SEO
        title={`${property.bhk} BHK ${property.property_type} ${isRent ? "for rent" : "for sale"} in ${property.locality}, ${property.city}`}
        description={`${property.title} · ${property.area_sqft} sqft · ${priceLabel}${isRent ? "/month" : ""}. ${property.description?.slice(0, 140) || ""} Contact the owner directly on To-Let Rentals. Zero brokerage.`}
        path={`/property/${property.id}`}
        image={shareImage}
        keywords={`${property.bhk} BHK ${property.city}, ${property.property_type} ${property.locality}, ${isRent ? "rent" : "sale"} ${property.city}, no brokerage ${property.locality}, ${property.furnishing} ${property.property_type}`}
        breadcrumbs={[
          { name: isRent ? "Rent" : "Buy", path: `/properties?listing_type=${property.listing_type}` },
          { name: property.city, path: `/rent/${property.city.toLowerCase()}` },
          { name: property.locality, path: `/properties?listing_type=${property.listing_type}&city=${encodeURIComponent(property.city)}&locality=${encodeURIComponent(property.locality)}` },
          { name: property.title, path: `/property/${property.id}` },
        ]}
        schema={{
          "@context": "https://schema.org",
          "@type": "RealEstateListing",
          "@id": `https://to-let-rentals.com/property/${property.id}`,
          "url": `https://to-let-rentals.com/property/${property.id}`,
          "name": property.title,
          "description": property.description,
          "datePosted": property.created_at || undefined,
          "image": images.slice(0, 8).map((u) => (u.startsWith("http") ? u : `https://to-let-rentals.com${u}`)),
          "address": {
            "@type": "PostalAddress",
            "streetAddress": property.address,
            "addressLocality": property.locality,
            "addressRegion": property.city,
            "addressCountry": "IN",
          },
          "geo": property.lat && property.lng ? {
            "@type": "GeoCoordinates",
            "latitude": property.lat,
            "longitude": property.lng,
          } : undefined,
          "numberOfBedrooms": property.bhk,
          "numberOfBathroomsTotal": property.bathrooms,
          "floorSize": { "@type": "QuantitativeValue", "value": property.area_sqft, "unitCode": "FTK" },
          "petsAllowed": property.pets_allowed || undefined,
          "amenityFeature": (property.amenities || []).map((a) => ({
            "@type": "LocationFeatureSpecification",
            "name": a,
            "value": true,
          })),
          "mainEntity": {
            "@type": "Accommodation",
            "@type_alt": isRent ? "Apartment" : "House",
            "floorLevel": property.floor || undefined,
            "occupancy": { "@type": "QuantitativeValue", "value": property.bhk },
          },
          "offers": {
            "@type": "Offer",
            "price": property.price,
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "url": `https://to-let-rentals.com/property/${property.id}`,
            "seller": {
              "@type": "Organization",
              "name": "To-Let Rentals",
              "url": "https://to-let-rentals.com",
            },
            "priceSpecification": isRent ? {
              "@type": "UnitPriceSpecification",
              "price": property.price,
              "priceCurrency": "INR",
              "unitText": "MONTH",
              "billingIncrement": 1,
            } : undefined,
          },
          "broker": {
            "@type": "Organization",
            "name": "To-Let Rentals",
            "description": "Zero brokerage rentals platform",
            "url": "https://to-let-rentals.com",
          },
        }}
      />
      <Breadcrumbs items={[
        { name: isRent ? "Rent" : "Buy", path: `/properties?listing_type=${property.listing_type}` },
        { name: property.city, path: `/rent/${property.city.toLowerCase()}` },
        { name: property.title, path: `/property/${property.id}` },
      ]} />
      {/* Bento gallery — mobile: single 280px image, no empty second row. Desktop: 3-col × 2-row bento.
          Every tile is a button that opens the lightbox at its index. */}
      <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-3 h-[280px] md:h-[520px] mb-4 md:mb-10 relative">
        <button
          type="button"
          onClick={() => setLightboxAt(0)}
          data-testid="gallery-tile-0"
          className="md:col-span-2 md:row-span-2 rounded-2xl overflow-hidden bg-gray-100 relative group cursor-zoom-in"
          aria-label="Open photo 1 fullscreen"
        >
          <img src={images[0]} alt={property.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" data-testid="prop-main-image" />
          {/* Mobile-only 'View all photos' overlay chip — desktop uses the bento tiles */}
          {images.length > 1 && (
            <div className="md:hidden absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur text-white text-xs font-semibold">
              <Images size={12} /> View all {images.length} photos
            </div>
          )}
        </button>
        {images[1] && (
          <button
            type="button"
            onClick={() => setLightboxAt(1)}
            data-testid="gallery-tile-1"
            className="rounded-2xl overflow-hidden bg-gray-100 hidden md:block group cursor-zoom-in"
            aria-label="Open photo 2 fullscreen"
          >
            <img src={images[1]} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
          </button>
        )}
        {images[2] && (
          <button
            type="button"
            onClick={() => setLightboxAt(2)}
            data-testid="gallery-tile-2"
            className="rounded-2xl overflow-hidden bg-gray-100 hidden md:block relative group cursor-zoom-in"
            aria-label="Open photo 3 fullscreen"
          >
            <img src={images[2]} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
            {images.length > 3 && (
              <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 flex items-center justify-center text-white transition-colors">
                <span className="flex items-center gap-2 font-display text-xl">
                  <Images size={18} /> +{images.length - 3} more
                </span>
              </div>
            )}
          </button>
        )}
      </div>

      {/* Fullscreen lightbox — keyboard nav, swipe, thumbnails */}
      {lightboxAt !== null && (
        <PropertyGalleryLightbox
          images={images}
          startIndex={lightboxAt}
          onClose={() => setLightboxAt(null)}
        />
      )}

      <div className="grid lg:grid-cols-3 gap-6 md:gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase ${
              isRent ? "bg-[#7A1E2A] text-white" : "bg-[#0EA5E9] text-white"
            }`}>{isRent ? "For Rent" : "For Sale"}</span>
            <span className="chip">{property.property_type}</span>
            <span className="chip">{property.furnishing}</span>
            {property.moderation_status === "pending" && (
              <span data-testid="moderation-pending-badge" className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-bold tracking-wider uppercase">
                <Clock size={11} /> Awaiting approval
              </span>
            )}
            {property.moderation_status === "rejected" && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-100 text-red-700 text-[11px] font-bold tracking-wider uppercase">
                <XCircle size={11} /> Rejected
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl brand-forest mb-2 md:mb-3" data-testid="prop-title">{property.title}</h1>
          <p className="flex items-center gap-2 text-gray-500 mb-3 md:mb-4 text-sm md:text-base"><MapPin size={16} /> {property.address}, {property.locality}, {property.city}</p>

          {/* Activity strip — trust-building live signals. Fire when the
              listing has any recorded views, enquiries, or a known post date.
              We do NOT fabricate numbers when they're zero; a brand-new
              listing simply shows fewer chips (or none). */}
          {(property.view_count > 0 || property.contact_count > 0 || property.created_at) && (
            <div
              data-testid="prop-activity-strip"
              className="flex flex-wrap items-center gap-2 mb-5 md:mb-8"
            >
              {property.view_count > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#FBF6EC] text-[#1B0F14] border border-[#EFE3D0]">
                  <Eye size={13} className="text-[#7A1E2A]" />
                  {property.view_count.toLocaleString("en-IN")} views
                </span>
              )}
              {property.contact_count > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#7A1E2A]/10 text-[#7A1E2A] border border-[#7A1E2A]/20">
                  <MessageSquare size={13} />
                  {property.contact_count} enquir{property.contact_count === 1 ? "y" : "ies"}
                </span>
              )}
              {(property.contact_count >= 20 || property.view_count >= 350) && (
                <span
                  data-testid="prop-trending-badge"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#FF6B35] to-[#C9302C] text-white shadow-sm"
                >
                  <Flame size={13} className="fill-white" /> Trending in {property.city}
                </span>
              )}
              {property.created_at && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-gray-600 bg-white border border-[#EFE3D0]">
                  <Clock size={13} className="text-gray-500" />
                  Posted {_timeAgoLong(property.created_at)}
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-10">
            {[
              { icon: <BedDouble size={18} />, label: "Bedrooms", value: `${property.bhk} BHK` },
              { icon: <Bath size={18} />, label: "Bathrooms", value: property.bathrooms },
              { icon: <Ruler size={18} />, label: "Area", value: `${property.area_sqft} sqft` },
              { icon: <Home size={18} />, label: "Type", value: property.property_type },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-brand-subtle p-3 md:p-5">
                <div className="brand-forest mb-2 md:mb-3">{s.icon}</div>
                <p className="text-[10px] md:text-xs uppercase tracking-widest text-gray-500">{s.label}</p>
                <p className="font-display text-base md:text-xl brand-forest capitalize">{s.value}</p>
              </div>
            ))}
          </div>

          <h2 className="font-display text-xl md:text-2xl brand-forest mb-2 md:mb-3">About this property</h2>
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap mb-6 md:mb-10 text-sm md:text-base">{property.description}</p>

          {(() => {
            const embed = embedUrlFor(property.video_url);
            if (!embed) return null;
            return (
              <div className="mb-10" data-testid="prop-video-section">
                <h2 className="font-display text-2xl brand-forest mb-4 flex items-center gap-2">
                  <PlayCircle size={22} className="text-[#7A1E2A]" /> Video walkthrough
                </h2>
                <div className="relative w-full rounded-2xl overflow-hidden bg-black aspect-video">
                  {embed.kind === "video" ? (
                    <video src={embed.src} controls playsInline preload="metadata" className="w-full h-full" data-testid="prop-video-player" />
                  ) : (
                    <iframe
                      src={embed.src}
                      title="Property video walkthrough"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="prop-video-iframe"
                      className="w-full h-full border-0"
                    />
                  )}
                </div>
              </div>
            );
          })()}

          {property.amenities?.length > 0 && (
            <>
              <h2 className="font-display text-2xl brand-forest mb-4">Amenities</h2>
              <div className="flex flex-wrap gap-2 mb-10">
                {property.amenities.map((a) => (
                  <span key={a} className="chip capitalize">{a}</span>
                ))}
              </div>
            </>
          )}

          {property.available_from && (
            <p className="flex items-center gap-2 text-gray-600"><CalendarDays size={16} /> Available from <b>{property.available_from}</b></p>
          )}
        </div>

        {/* Sticky booking card */}
        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-2xl border border-brand-subtle p-6 shadow-sm">
            <p className="label-eyebrow text-gray-500 mb-1">{isRent ? "Monthly rent" : "Sale price"}</p>
            <p className="font-display text-4xl brand-forest mb-1" data-testid="prop-price">
              {priceLabel}{isRent && <span className="text-lg text-gray-500 font-body">/mo</span>}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Listed by <b>{property.owner_name || "Owner"}</b>
              {property.owner_verified && (
                <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold" data-testid="detail-verified-badge">
                  <BadgeCheck size={12} className="fill-emerald-600 text-white" /> Verified
                </span>
              )}
            </p>

            {contact ? (
              <div className="bg-[#FFEED9] rounded-xl p-4 mb-4 fade-in space-y-3" data-testid="contact-info">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-1">Owner phone</p>
                  <p className="font-display text-2xl brand-forest">{contact.phone || "N/A"}</p>
                  <p className="text-xs text-gray-500 mt-2 uppercase tracking-widest font-semibold mb-1">Owner email</p>
                  <p className="font-body text-sm text-gray-700 break-all">{contact.email}</p>
                </div>
                {contact.phone && (
                  <div className="grid grid-cols-3 gap-2">
                    <a
                      href={`tel:${contact.phone}`}
                      data-testid="call-now-btn"
                      className="flex flex-col items-center justify-center py-3 rounded-xl bg-brand-forest text-white hover:bg-[#E64520] font-semibold text-sm transition-colors"
                    >
                      <Phone size={18} />
                      <span className="mt-1">Call</span>
                    </a>
                    <a
                      href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi, I'm interested in your listing on To-Let Rentals: ${property.title}`)}`}
                      target="_blank" rel="noreferrer"
                      data-testid="whatsapp-btn"
                      className="flex flex-col items-center justify-center py-3 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 font-semibold text-sm transition-colors"
                    >
                      <MessageCircle size={18} />
                      <span className="mt-1">WhatsApp</span>
                    </a>
                    <a
                      href={`mailto:${contact.email}?subject=${encodeURIComponent(`Enquiry about ${property.title} on To-Let Rentals`)}`}
                      data-testid="email-btn"
                      className="flex flex-col items-center justify-center py-3 rounded-xl bg-[#0EA5E9] text-white hover:bg-[#0284C7] font-semibold text-sm transition-colors"
                    >
                      <Mail size={18} />
                      <span className="mt-1">Email</span>
                    </a>
                  </div>
                )}
              </div>
            ) : isOwner ? (
              <div className="bg-[#FFEED9] rounded-xl p-4 mb-4 text-sm text-gray-700" data-testid="owner-self-note">
                <b>This is your listing.</b> Renters see a "Reveal contact" button that shares your phone and email.
              </div>
            ) : (
              <Button
                data-testid="reveal-contact-btn"
                onClick={reveal} disabled={revealing}
                className="w-full tlr-gold-btn rounded-full h-12 mb-3"
              >
                <Phone size={16} className="mr-2" />
                {revealing ? "Revealing…" : "Reveal owner contact"}
              </Button>
            )}

            {isOwner && boostOn && (
              <Button
                data-testid="boost-cta-owner"
                onClick={() => setBoostOpen(true)}
                className={`w-full rounded-full h-12 font-semibold mb-3 ${
                  property.is_featured
                    ? "bg-[#E0F2FE] text-[#0EA5E9] hover:bg-[#BAE6FD] border border-[#BAE6FD]"
                    : "bg-brand-forest hover:bg-[#E64520] text-white"
                }`}
              >
                {property.is_featured ? <><Star size={16} className="mr-2 fill-[#0EA5E9]" /> Extend boost</> : <><Rocket size={16} className="mr-2" /> Boost to top of search</>}
              </Button>
            )}

            <Button
              data-testid="save-property-btn"
              onClick={toggleFav} variant="outline"
              className="w-full rounded-full h-11 border-brand-subtle"
            >
              <Heart size={16} className={`mr-2 ${isFav ? "fill-[#0EA5E9] text-[#0EA5E9]" : ""}`} />
              {isFav ? "Saved" : "Save property"}
            </Button>

            <ShareBar property={property} priceLabel={priceLabel} isRent={isRent} />

            {user && !user.is_unlimited && (
              <div className="mt-5 pt-5 border-t border-brand-subtle text-xs text-gray-500 space-y-1" data-testid="reveal-quota">
                <div className="flex items-center gap-2"><ShieldCheck size={14} className="brand-forest" /> Free reveals: <b>{user.free_reveals_used}/{user.free_reveals_limit}</b></div>
                {user.contacts_remaining > 0 && <div className="flex items-center gap-2"><Sparkles size={14} className="brand-terracotta" /> Paid credits: <b>{user.contacts_remaining}</b></div>}
                {remaining === 0 && (
                  <Link to="/pricing" className="brand-terracotta font-semibold hover:underline block mt-2">
                    Upgrade for more contacts →
                  </Link>
                )}
              </div>
            )}
            {user?.is_unlimited && (
              <div className="mt-5 pt-5 border-t border-brand-subtle text-xs text-emerald-700 font-semibold">
                ✓ Unlimited reveals active
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Related properties — SEO + retention win */}
      <div className="mt-10">
        <ListingConcierge property={property} isOwner={isOwner} />
      </div>
      <RelatedProperties propertyId={property.id} cityName={property.city} />

      <BoostDialog property={property} open={boostOpen} onOpenChange={setBoostOpen} />

      {/* Sticky mobile action bar — keeps the primary CTA reachable without scrolling */}
      {!isOwner && !contact && (
        <>
          <div className="h-20 md:hidden" aria-hidden />
          <div
            data-testid="mobile-contact-bar"
            className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-brand-subtle px-4 py-3 flex items-center gap-3 shadow-[0_-6px_20px_-8px_rgba(0,0,0,0.15)]"
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          >
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 leading-none mb-0.5">{isRent ? "Monthly rent" : "Sale price"}</p>
              <p className="font-display text-xl brand-forest leading-tight truncate">
                {priceLabel}{isRent && <span className="text-sm text-gray-500 font-body">/mo</span>}
              </p>
            </div>
            <Button
              data-testid="mobile-reveal-contact-btn"
              onClick={reveal}
              disabled={revealing}
              className="ml-auto tlr-gold-btn rounded-full h-11 px-5 shrink-0"
            >
              <Phone size={16} className="mr-2" />
              {revealing ? "Revealing…" : "Reveal contact"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
